import { useEffect, useState, useCallback } from "react";
import { readConsent, writeConsent, applyConsentToGtag } from "../lib/cookieConsent";

/**
 * Minimal RGPD cookie consent banner integrated with Google Consent Mode v2.
 * Default state (set in index.html): all denied.
 * On accept → update consent to granted. On refuse → keep denied.
 *
 * Storage rationale: the consent state ("granted"|"denied") is NOT sensitive data —
 * it's the user's RGPD choice that MUST persist across sessions to comply with the
 * "ask only once" requirement. localStorage is the recommended primitive.
 */
export default function CookieConsent() {
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            const saved = readConsent();
            if (cancelled) return;
            if (!saved) {
                setVisible(true);
                return;
            }
            if (saved === "granted") applyConsentToGtag("granted");
        })();
        return () => { cancelled = true; };
    }, []);

    const persist = useCallback((state) => {
        writeConsent(state);
        applyConsentToGtag(state);
        setVisible(false);
    }, []);

    if (!visible) return null;

    return (
        <div
            role="dialog"
            aria-live="polite"
            aria-label="Préférences cookies"
            className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:max-w-sm z-50 bg-[var(--navy)] text-[var(--cream)] rounded-2xl shadow-2xl border border-white/10 p-5"
            data-testid="cookie-consent-banner"
        >
            <p className="heading text-sm font-bold text-[var(--cream)]">🍪 Cookies &amp; mesure d&apos;audience</p>
            <p className="text-xs text-[var(--cream)]/80 mt-2 leading-relaxed">
                Nous utilisons des cookies pour mesurer l&apos;audience de notre site (Google Analytics) et améliorer votre expérience.
                Vous pouvez refuser sans impact sur le service.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
                <button
                    type="button"
                    onClick={() => persist("granted")}
                    className="btn-amber inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-bold flex-1"
                    data-testid="cookie-accept"
                >
                    Accepter
                </button>
                <button
                    type="button"
                    onClick={() => persist("denied")}
                    className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border border-[var(--amber)]/40 text-[var(--amber)] hover:bg-[var(--amber)]/10 transition flex-1"
                    data-testid="cookie-refuse"
                >
                    Refuser
                </button>
            </div>
        </div>
    );
}
